import React from 'react'
import { useIntl } from 'react-intl'
import classNames from 'classnames'
import Checkbox from '@material-ui/core/Checkbox/Checkbox'
import Chip from '@material-ui/core/Chip'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import CheckBoxOutlineBlankOutlinedIcon from '@material-ui/icons/CheckBoxOutlineBlankOutlined'
import CheckBoxOutlinedIcon from '@material-ui/icons/CheckBoxOutlined'
import CloseIcon from '@material-ui/icons/Close'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import useStyles from './styles'

const CustomSelect = ({
  form,
  formFieldName,
  labelClasses,
  inputClasses,
  labelId,
  inputProps = null,
  inputVariant = 'standard',
  autoComplete = 'off',
  required = false,
  disabled = false,
  serviceError = false,
  items,
  isMultiple = false,
  nonRemovableValues = []
}) => {
  const classes = useStyles()
  const [open, setOpen] = React.useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleSelectChange = (e) => {
    const newValue = e.target.value || []
    const current = form.values[formFieldName] || []
    const keptNonRemovable = current.filter((v) => nonRemovableValues.includes(v))
    const merged = [...new Set([...keptNonRemovable, ...newValue])]
    form.setFieldValue(formFieldName, merged)
    form.setFieldTouched(formFieldName, true)
  }

  const isItemSelected = (currentValue, itemValue) => {
    if (isMultiple) {
      return Array.isArray(currentValue) && currentValue.includes(itemValue)
    }
    return currentValue === itemValue
  }

  const handleMenuItemClick = (itemValue) => (e) => {
    const currentValue = form.values[formFieldName]
    const isSelected = isItemSelected(currentValue, itemValue)
    if (nonRemovableValues.includes(itemValue) && isSelected) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const handleCloseChip = (key) => {
    if (nonRemovableValues.includes(key)) return
    form.setFieldValue(
      formFieldName,
      form.values[formFieldName].filter((value) => value !== key)
    )
  }

  const closeAllChips = () => {
    const current = form.values[formFieldName] || []
    const kept = current.filter((value) => nonRemovableValues.includes(value))
    form.setFieldValue(formFieldName, kept)
  }

  const returnChip = (value) => {
    const selectedItem = items.find((item) => item.value === value)
    if (!selectedItem) return null
    const isNonRemovable = nonRemovableValues.includes(value)
    const canDelete = !disabled && !isNonRemovable
    return (
      <Chip
        disabled={disabled}
        key={selectedItem.value}
        size={'small'}
        label={selectedItem.label}
        onMouseDown={(e) => e.stopPropagation()}
        onDelete={canDelete ? () => handleCloseChip(selectedItem.value) : undefined}
        className={classes.chip}
        deleteIcon={canDelete ? <CloseIcon onClick={() => handleCloseChip(selectedItem.value)} /> : <></>}
      />
    )
  }

  const renderChips = (selected) => <div className={classes.chips}>{selected.map((value) => returnChip(value))}</div>

  const renderSimpleValue = (selected) =>
    items.find((item) => item.value === selected) ? items.find((item) => item.value === selected).label : ''

  const renderValue = (selected) => (isMultiple ? renderChips(selected) : renderSimpleValue(selected))

  return (
    <TextField
      select
      SelectProps={{
        endAdornment:
          isMultiple && !disabled ? (
            <CloseIcon
              onClick={disabled ? undefined : closeAllChips}
              className={classNames(classes.deleteChips, { [classes.pointer]: !disabled })}
            />
          ) : (
            ''
          ),
        MenuProps: {
          classes: {
            paper: classes.paper
          }
        },
        open,
        onOpen: () => handleOpen(),
        onClose: () => handleClose(),
        IconComponent: (props) => (
          <KeyboardArrowDownIcon
            {...props}
            className={classNames(classes.icon, { [classes.pointer]: !disabled })}
            onClick={disabled ? undefined : handleOpen}
            onMouseDown={disabled ? undefined : handleOpen}
          />
        ),
        multiple: isMultiple,
        classes: {
          select: classes.select
        },
        renderValue: (selected) => renderValue(selected)
      }}
      FormHelperTextProps={{
        className: classes.errorMessage
      }}
      InputLabelProps={{
        disableAnimation: true,
        shrink: true,
        classes: {
          shrink: classes.shrink,
          formControl: classes.label,
          ...labelClasses
        }
      }}
      InputProps={{
        disableUnderline: true,
        classes: {
          root: classes.root,
          input: classes.input,
          ...inputClasses
        },
        ...inputProps
      }}
      id={formFieldName}
      name={formFieldName}
      value={form.values[formFieldName]}
      onChange={isMultiple && nonRemovableValues.length > 0 ? handleSelectChange : form.handleChange}
      onBlur={form.handleBlur}
      error={(form.touched[formFieldName] && Boolean(form.errors[formFieldName])) || serviceError}
      helperText={form.touched[formFieldName] && form.errors[formFieldName]}
      label={useIntl().formatMessage({ id: labelId })}
      variant={inputVariant}
      fullWidth
      margin="normal"
      autoComplete={autoComplete}
      required={required}
      disabled={disabled}
    >
      {items.map((item) => {
        const currentValue = form.values[formFieldName]
        const isSelected = isItemSelected(currentValue, item.value)
        const isNonRemovable = nonRemovableValues.includes(item.value)
        const isCheckboxDisabled = isMultiple && isNonRemovable && isSelected
        return (
          <MenuItem
            key={item.value}
            value={item.value}
            className={classNames(classes.menuItem, isCheckboxDisabled && classes.menuItemDisabled)}
            onClick={handleMenuItemClick(item.value)}
          >
            {isMultiple && (
              <Checkbox
                icon={<CheckBoxOutlineBlankOutlinedIcon />}
                checkedIcon={
                  <CheckBoxOutlinedIcon
                    className={classNames(isCheckboxDisabled ? classes.checkedLocked : classes.checked)}
                  />
                }
                checked={isSelected}
                className={classNames(classes.checkbox, isCheckboxDisabled && classes.checkboxDisabled)}
                disableRipple
                disabled={isCheckboxDisabled}
              />
            )}
            {item.label}
          </MenuItem>
        )
      })}
    </TextField>
  )
}

export default CustomSelect
